import React, { useState, useEffect, useRef } from 'react';
import { chatAPI } from '../services/api';
import { FiSend, FiMessageCircle, FiX, FiMinimize2, FiRefreshCw } from 'react-icons/fi';
import ContactForm from './ContactForm';
import ProductCard from './ProductCard';
import '../styles/ChatWidget.css';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [products, setProducts] = useState([]);
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captchaData, setCaptchaData] = useState(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showQuickReplies, setShowQuickReplies] = useState(true);
  const messagesEndRef = useRef(null);

  // Quick reply options
  const quickReplyOptions = [
    { id: 1, icon: '🛍️', text: 'Show Products', message: 'Show me your products' },
    { id: 2, icon: '💰', text: 'View Deals', message: 'What products are on sale?' },
    { id: 3, icon: '📦', text: 'Shipping Info', message: 'What is your shipping policy?' },
    { id: 4, icon: '📞', text: 'Contact Us', message: 'contact_form_request', isContactForm: true },
  ];

  // Initialize chat session
  useEffect(() => {
    const initSession = async () => {
      try {
        const storedSessionId = localStorage.getItem('chatSessionId');
        
        if (storedSessionId) {
          setSessionId(storedSessionId);
          // Load chat history
          const response = await chatAPI.getChatHistory(storedSessionId, 20);
          if (response.success && response.data.length > 0) {
            setMessages(response.data);
            setShowQuickReplies(false); // Hide if there's history
          } else {
            // Has sessionId but no history - show welcome
            setMessages([{
              sender: 'bot',
              message: 'Hello! 👋 Welcome to our store. How can I help you today?',
              timestamp: new Date()
            }]);
          }
        } else {
          // Show loading state immediately to avoid blank screen
          setMessages([{
            sender: 'bot',
            message: 'Loading chat...',
            timestamp: new Date()
          }]);
          
          const response = await chatAPI.createSession();
          if (response.success) {
            setSessionId(response.sessionId);
            localStorage.setItem('chatSessionId', response.sessionId);
            // Replace loading message with welcome
            setMessages([{
              sender: 'bot',
              message: 'Hello! 👋 Welcome to our store. How can I help you today?',
              timestamp: new Date()
            }]);
          }
        }
      } catch (error) {
        console.error('Error initializing session:', error);
        setMessages([{
          sender: 'bot',
          message: '❌ Failed to connect. Please try refreshing the page.',
          timestamp: new Date()
        }]);
      }
    };

    if (isOpen && !sessionId) {
      initSession();
    }
  }, [isOpen, sessionId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (messageText = null) => {
    const userMessage = messageText || inputMessage.trim();
    if (!userMessage || isLoading) return;

    setInputMessage('');
    setIsLoading(true);
    setErrorMessage('');
    setShowQuickReplies(false); // Hide quick replies after first interaction
    setProducts([]); // Clear previous products

    // Add user message to UI
    setMessages(prev => [...prev, {
      sender: 'user',
      message: userMessage,
      timestamp: new Date()
    }]);

    try {
      const requestBody = {
        sessionId: sessionId,
        message: userMessage
      };

      // Add CAPTCHA if required
      if (captchaRequired && captchaData) {
        if (!captchaAnswer) {
          setErrorMessage('Please solve the CAPTCHA first');
          setIsLoading(false);
          return;
        }
        requestBody.captchaToken = captchaData.captchaToken;
        requestBody.captchaAnswer = captchaAnswer;
      }

      const response = await chatAPI.sendMessage(sessionId, userMessage, requestBody);
      
      // Handle rate limiting
      if (response.blocked) {
        setMessages(prev => [...prev, {
          sender: 'bot',
          message: '⛔ ' + (response.message || 'Too many requests. Please slow down.'),
          timestamp: new Date()
        }]);
        return;
      }

      // Handle CAPTCHA requirement
      if (response.requireCaptcha) {
        setCaptchaRequired(true);
        await fetchCaptcha();
        setMessages(prev => [...prev, {
          sender: 'bot',
          message: '🔒 Security verification required. Please solve the math problem to continue.',
          timestamp: new Date()
        }]);
        return;
      }

      if (response.success) {
        // CAPTCHA passed - reset
        if (captchaRequired) {
          setCaptchaRequired(false);
          setCaptchaData(null);
          setCaptchaAnswer('');
        }

        // Add bot response
        setMessages(prev => [...prev, {
          sender: 'bot',
          message: response.data.message,
          timestamp: new Date()
        }]);

        // Show products if any
        console.log('Products from API:', response.data.products);
        if (response.data.products && response.data.products.length > 0) {
          console.log('✅ Setting products:', response.data.products.length, 'products');
          console.log('Product IDs:', response.data.products.map(p => p.id).join(', '));
          setProducts(response.data.products);
          
          // Scroll to products after a short delay
          setTimeout(scrollToBottom, 300);
        } else {
          console.log('❌ No products found, clearing');
          setProducts([]);
        }

        // Check if CAPTCHA still required
        if (response.requireCaptcha) {
          setCaptchaRequired(true);
          await fetchCaptcha();
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        sender: 'bot',
        message: '❌ Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCaptcha = async () => {
    try {
      const response = await fetch(`http://localhost:5000/api/chat/captcha?sessionId=${sessionId}`);
      const data = await response.json();
      if (data.success) {
        setCaptchaData(data);
        setCaptchaAnswer('');
      }
    } catch (error) {
      console.error('Error fetching CAPTCHA:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(null);
    }
  };

  const handleQuickReply = (option) => {
    // Handle contact form request
    if (option.isContactForm) {
      // Add user message
      const userMessage = {
        sender: 'user',
        message: option.message,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, userMessage]);
      setShowQuickReplies(false);

      // Add bot message for contact form
      const contactMessage = {
        sender: 'bot',
        message: "I'm here to help! Please feel free to ask any questions or let me know how I can assist you today. If you have any specific product requests or need further assistance, don't hesitate to reach out.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, contactMessage]);
      setShowContactForm(true);
    } else {
      // Handle normal message through handleSendMessage
      handleSendMessage(option.message);
    }
  };

  const handleContactFormSubmit = () => {
    setShowContactForm(false);
    setMessages(prev => [...prev, {
      sender: 'bot',
      message: 'Thank you for your submission! We will contact you soon.',
      timestamp: new Date()
    }]);
  };

  const handleRestartChat = async () => {
    // Create new session first before clearing
    try {
      const response = await chatAPI.createSession();
      if (response.success) {
        // Clear old session from localStorage
        localStorage.removeItem('chatSessionId');
        
        // Set new session ID first
        setSessionId(response.sessionId);
        localStorage.setItem('chatSessionId', response.sessionId);
        
        // Reset ALL states with welcome message to avoid blank screen
        setMessages([{
          sender: 'bot',
          message: 'Hello! 👋 Welcome to our store. How can I help you today?',
          timestamp: new Date()
        }]);
        setProducts([]);
        setShowContactForm(false);
        setShowQuickReplies(true);
        setCaptchaRequired(false);
        setCaptchaData(null);
        setCaptchaAnswer('');
        setErrorMessage('');
        setInputMessage('');
      }
    } catch (error) {
      console.error('Error restarting chat:', error);
      // Show error instead of blank screen
      setMessages([{
        sender: 'bot',
        message: '❌ Error starting new chat. Please refresh the page.',
        timestamp: new Date()
      }]);
    }
  };

  return (
    <div className="chat-widget-container">
      {/* Chat Button */}
      {!isOpen && (
        <button 
          className="chat-button"
          onClick={() => setIsOpen(true)}
          aria-label="Open chat"
        >
          <FiMessageCircle size={24} />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className={`chat-window ${isMinimized ? 'minimized' : ''}`}>
          {/* Header */}
          <div className="chat-header">
            <div className="chat-header-content">
              <FiMessageCircle size={20} />
              <span>Chat Support</span>
            </div>
            <div className="chat-header-actions">
              <button 
                onClick={handleRestartChat}
                aria-label="Restart chat"
                title="Start new conversation"
                className="restart-btn"
              >
                <FiRefreshCw size={18} />
              </button>
              <button 
                onClick={() => setIsMinimized(!isMinimized)}
                aria-label="Minimize chat"
              >
                <FiMinimize2 size={18} />
              </button>
              <button 
                onClick={() => setIsOpen(false)}
                aria-label="Close chat"
              >
                <FiX size={20} />
              </button>
            </div>
          </div>

          {/* Chat Body */}
          {!isMinimized && (
            <>
              <div className="chat-body">
                {/* Messages */}
                {messages.map((msg, index) => (
                  <div 
                    key={index} 
                    className={`message ${msg.sender}`}
                  >
                    <div className="message-content">
                      {msg.message}
                    </div>
                    <div className="message-time">
                      {new Date(msg.timestamp).toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </div>
                  </div>
                ))}

                {/* Loading indicator - Right after messages */}
                {isLoading && (
                  <div className="message bot">
                    <div className="message-content typing">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                )}

                {/* Quick Reply Buttons */}
                {showQuickReplies && messages.length === 1 && !isLoading && (
                  <div className="quick-replies-container">
                    <div className="quick-replies-title">Choose a topic:</div>
                    <div className="quick-replies">
                      {quickReplyOptions.map((option) => (
                        <button
                          key={option.id}
                          className="quick-reply-btn"
                          onClick={() => handleQuickReply(option)}
                        >
                          <span className="quick-reply-icon">{option.icon}</span>
                          <span className="quick-reply-text">{option.text}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Show products */}
                {products && products.length > 0 && (
                  <div className="products-container">
                    <div className="products-header">
                      <strong>Found {products.length} product{products.length !== 1 ? 's' : ''}</strong>
                    </div>
                    {products.map((product) => (
                      <ProductCard key={product.id} product={product} />
                    ))}
                  </div>
                )}

                {/* Contact form */}
                {showContactForm && (
                  <ContactForm 
                    sessionId={sessionId}
                    onSubmit={handleContactFormSubmit}
                    onClose={() => setShowContactForm(false)}
                  />
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="chat-input-container">
                {/* CAPTCHA Section */}
                {captchaRequired && captchaData && (
                  <div className="captcha-container">
                    <div className="captcha-title">🔒 Security Verification</div>
                    <div className="captcha-question">
                      {captchaData.question} = ?
                    </div>
                    <input
                      type="number"
                      className="captcha-input"
                      value={captchaAnswer}
                      onChange={(e) => setCaptchaAnswer(e.target.value)}
                      placeholder="Enter answer"
                    />
                    <div className="captcha-hint">
                      Solve the math problem to continue chatting
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {errorMessage && (
                  <div className="error-message-banner">
                    {errorMessage}
                  </div>
                )}

                <div className="input-wrapper">
                  <textarea
                    className="chat-input"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message..."
                    rows="1"
                    disabled={isLoading}
                  />
                  <button 
                    className="send-button"
                    onClick={() => handleSendMessage(null)}
                    disabled={!inputMessage.trim() || isLoading}
                    aria-label="Send message"
                  >
                    <FiSend size={20} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
